import { useEffect, useId, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { route } from 'ziggy-js';
import BezelFrame from '@/Components/Marketing/BezelFrame';
import Button from '@/Components/Marketing/Button';
import Container from '@/Components/Marketing/Container';
import Eyebrow from '@/Components/Marketing/Eyebrow';
import LazyBoundary from '@/Components/Marketing/LazyBoundary';
import { demoHref, livePassportUrl } from '@/Components/Marketing/links';
import { HeroBackdrop } from '@/Components/Marketing/Texture';
import { Display, Lead } from '@/Components/Marketing/Typography';
import useMediaQuery from '@/Components/Marketing/useMediaQuery';
import HeroStory, { HERO_HEIGHT, HERO_PART_LOADERS, HERO_PHONE_ANCHOR, useHeroStory } from '@/Components/Marketing/Vignettes/HeroStory';
import { useParts } from '@/Components/Marketing/Vignettes/lazyParts';
import { CROP_FLOOR, CROP_SCALES, HOME_CROP_VIEW } from '@/Components/Marketing/Vignettes/stage';

const PHONE_QUERY = '(min-width: 640px)';

/**
 * Where the phone sits on the window frame, as shares of the window (the
 * frame inside the bezel's 6px tray): centred on the passport's summary
 * rail (HERO_PHONE_ANCHOR: the page is laid out at one width and scaled, so
 * the shares hold at every width). Its top edge falls where no line of the
 * rail's text is cut, in the draft and after the release alike, and it
 * hangs below the window's bottom edge.
 */
const PHONE_ANCHOR = { centerX: HERO_PHONE_ANCHOR.x / 1200, top: HERO_PHONE_ANCHOR.y / HERO_HEIGHT[1200] };
const PHONE_WIDTH = 'min(20%, 250px)';

/**
 * How far the product pair reaches into the section below it from lg up.
 * The partner strip pads its top by this plus its own spacing (see
 * Partners.jsx), so the two must change together.
 */
export const HERO_OVERLAP = 'lg:-mb-[120px]';

/**
 * Home's opening: the calendar eyebrow, the headline, one line on what the
 * product does and the two CTAs, then the app itself in a large bezel frame
 * (a passport completed and released) with its public page on a phone
 * over the lower right corner. From lg the pair runs 120px into the partner
 * strip below.
 *
 * Nothing here starts hidden: the text and the passport are the page's
 * largest paint and are there from the first frame; the passport's own story
 * plays from a complete opening state.
 */
export default function Hero() {
    const { formatMessage } = useIntl();
    const t = id => formatMessage({ id });
    const titleId = useId();

    return (
        <section aria-labelledby={titleId} className="relative isolate z-10 pb-16 pt-32 sm:pb-24 sm:pt-36 lg:pb-0">
            <HeroBackdrop />
            <Container>
                <div className="max-w-4xl">
                    <Eyebrow href={route('outer.batterycompliancecalendar')}>{t('site.home.hero.eyebrow')}</Eyebrow>
                    <Display id={titleId} className="mt-6 max-w-[12em]">
                        {t('site.home.hero.title')}
                    </Display>
                    <Lead className="mt-6 max-w-[46ch]">{t('site.home.hero.lead')}</Lead>
                    <div className="mt-10 flex flex-wrap gap-3">
                        <Button href={demoHref()} size="lg">
                            {t('site.cta.demo')}
                        </Button>
                        <Button href={livePassportUrl()} variant="secondary" size="lg">
                            {t('site.cta.livePassport')}
                        </Button>
                    </div>
                </div>

                <Product />
            </Container>
        </section>
    );
}

/**
 * The passport view in the app, with the public passport on a phone over its
 * lower right, both following one story (useHeroStory: the window plays it,
 * the phone follows it). The phone's box is laid out before its chunk
 * arrives, so the page never moves when it does. It is anchored to the
 * window frame (PHONE_ANCHOR, in CSS: no measuring), so it keeps its place
 * on the drawn page at every width.
 */
function Product() {
    const wide = useMediaQuery(PHONE_QUERY);
    const story = useHeroStory();
    // The phone brings the public viewer with it (charts included): a part
    // like the story's own (see lazyParts), asked for only where the phone
    // shows (sm and up). Below the fold (a laptop's first screen) it waits
    // until the page has settled after loading; with its box in view, or
    // about to be (PHONE_MARGIN), it is asked for at once (R35). Mounted
    // after cues it missed, it opens where the story is.
    const box = useRef(null);
    const seen = useInView(box, wide);
    const { phone } = useParts([{ name: 'phone', loader: HERO_PART_LOADERS.phone, enabled: wide, now: seen }]);
    const Phone = wide ? phone?.default : null;
    const frame = (share, axis) => `calc(6px + (100% - 12px) * ${share}${axis === 'x' ? ` - ${PHONE_WIDTH} / 2` : ''})`;
    // A phone's crop: the camera's box, CROP_SCALES.home of the window's
    // width, or less (down to CROP_FLOOR) where the plate cannot show
    // HOME_CROP_VIEW of it at that scale.
    const cropped = `clamp(${1200 * CROP_FLOOR}px, ${((1200 / HOME_CROP_VIEW) * 100).toFixed(2)}%, ${1200 * CROP_SCALES.home}px)`;

    return (
        <div className={`relative mt-14 sm:mt-20 ${HERO_OVERLAP}`}>
            <BezelFrame shadow>
                {/* The desktop window at every width: its stage lays the app
                    out at desktop breakpoints, which a narrower layout would
                    crowd (VignetteFrame). On a phone, a readable crop of it
                    (CROP_SCALES: the camera's box) that follows the story
                    (PassportCues): the rows and the drawer while values
                    land, the rail at the release, where it rests under
                    reduced motion, its right edge on the plate's (no phone
                    beside it there). The box is kept at every width, so
                    crossing 640px remounts nothing; from 640px it is
                    style-neutral, any pan cleared. */}
                <div
                    data-vignette-camera={wide ? undefined : ''}
                    style={wide ? { transform: 'none' } : { width: cropped, marginLeft: story.reduced ? `calc(100% - ${cropped})` : undefined }}
                >
                    <HeroStory story={story} baseWidth={1200} />
                </div>
            </BezelFrame>
            <div
                ref={box}
                data-hero-phone=""
                className="absolute hidden w-[20%] max-w-[250px] sm:block"
                style={{ left: frame(PHONE_ANCHOR.centerX, 'x'), top: frame(PHONE_ANCHOR.top, 'y') }}
            >
                <BezelFrame shadow>
                    <div className="aspect-[9/19]">
                        <LazyBoundary>{Phone && <Phone story={story} />}</LazyBoundary>
                    </div>
                </BezelFrame>
            </div>
        </div>
    );
}

/** How close to the viewport the phone's box counts as in view. */
const PHONE_MARGIN = '64px';

/** Whether `ref`'s element has been in (or within PHONE_MARGIN of) the viewport, while `watch`. */
function useInView(ref, watch) {
    const [seen, setSeen] = useState(false);

    useEffect(() => {
        if (!watch || seen || !ref.current || typeof IntersectionObserver === 'undefined') return undefined;
        const observer = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) setSeen(true);
        }, { rootMargin: PHONE_MARGIN });
        observer.observe(ref.current);

        return () => observer.disconnect();
    }, [ref, watch, seen]);

    return seen;
}
